
import React, { useState, useEffect } from "react";
import "../styles/HotelsPage.css";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import SEO from "../components/SEO";

import * as productService from "../_services/products.service";

export default function MagyneCheckInsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(location.state?.product || null);

  const params = new URLSearchParams(location.search);

  const proceedTo = params.get("proceedTo") || "register";
  const tenantId = params.get("tenantId") || null;

  const productId =
    params.get("productId") ||
    "eb9d53c4-472c-4c70-8e8d-f9a5f7add291";

  useEffect(() => {
      const fetchProduct = async () => {
        if (!product && productId) {
          setLoading(true);
  
          try {
            const response = await productService.get({
              productId,
              status: "all"
            });
  
            if (!response.data?.length) {
              setProduct(null);
              return;
            }
  
            setProduct(response.data[0]);
          } catch (error) {
            toast.error(
              error?.message || "Failed to fetch product details."
            );
            setProduct(null);
          } finally {
            setLoading(false);
          }
        }
      };
  
      fetchProduct();
    }, [product, productId]);
  
    const getStatusMessage = () => {
      if (!product?.status) return null;
  
      switch (product.status.toUpperCase()) {
        case "PLANNED":
          return "Magyne Checkins is planned and will be available soon.";
  
        case "DEVELOPMENT":
          return "Magyne Checkins is Coming Soon.";
  
        case "ACTIVE":
          return null;
  
        default:
          return "Magyne Checkins is currently unavailable.";
      }
    };
  
    const isActive = product?.status?.toUpperCase() === "ACTIVE";
    
  const features = [
    {
      name: "Reservation Management",
      details:
        "Seamlessly handle bookings, cancellations, and modifications with real-time availability updates.",
      benefits: "Improves guest experience and reduces double bookings."
    },
    {
      name: "Billing & Invoicing",
      details:
        "Generate accurate bills for rooms, services, and amenities with multiple payment options.",
      benefits: "Ensures transparency and boosts operational efficiency."
    },
    {
      name: "Housekeeping Scheduling",
      details:
        "Organize and track housekeeping tasks to maintain spotless rooms and prompt service.",
      benefits: "Improves guest satisfaction and optimizes staff time."
    },
    {
      name: "Inventory & Procurement",
      details:
        "Track stock levels for linens, food, beverages, and supplies with automated alerts.",
      benefits: "Prevents shortages and minimizes wastage."
    }
  ];

  
  const handleNavigate = () => {
    if (!product) {
      toast.error("Product information is unavailable.");
      return;
    }

    const status = product.status?.toUpperCase();

    if (status !== "ACTIVE") {
      switch (status) {
        case "PLANNED":
          toast("Magyne Checkins is planned and is not yet available.", {
            icon: "🚀",
          });
          break;

        case "DEVELOPMENT":
          toast("Magyne Checkins is Coming Soon.", {
            icon: "🛠️",
          });
          break;

        default:
          toast.error("This product is currently unavailable.");
      }

      return;
    }

    navigate(
      `/pricing/?productId=${product.productId}&proceedTo=${proceedTo}&tenantId=${tenantId}`,
      {
        state: { product },
      }
    );
  };
  

  return (
    <>
      <SEO
        title="Magyne Checkins | Healthcare Management Software"
        description="Magyne Checkins is healthcare management software designed to help hospitals, clinics and healthcare organizations manage patients, administration and healthcare operations."
        path="/solutions/Checkins"
      />
    <main>
      <div className="hotels-page">
        {/* Hero Section */}
        
        {loading ? (
          <div>
            <div className="spinner"></div>

            <p className="text-center">
              Loading product.
              <br />
              Please wait...
            </p>
          </div>
        ) : !loading && product ? (
          <>
            <section
              className="hotels-hero"
              style={{ backgroundImage: `url('/images/hotel.jpg')` }}
            >
              <div className="hotels-hero-overlay">
                <h1>Hotel Management Solutions</h1>
                <p>
                  Elevate guest experiences with our all-in-one reservation, billing,
                  and service management system.
                </p>
                
                {!isActive && (
                  <div className="product-status-message">
                    {getStatusMessage()}
                  </div>
                )}

                <button
                  className="cta-btn"
                  onClick={handleNavigate}
                  disabled={!isActive}
                  title={
                    !isActive
                      ? getStatusMessage()
                      : "Choose a Magyne Checkins package"
                  }
                >
                  {product.status?.toUpperCase() === "PLANNED"
                    ? "Coming Soon"
                    : product.status?.toUpperCase() === "DEVELOPMENT"
                    ? "Coming Soon"
                    : "Choose a Package"}
                </button>

              </div>
            </section>

            {/* Features Section */}
            <section className="hotels-features">
              <h2>Premium Features</h2>
              <div className="feature-grid">
                {features.map((feature, idx) => (
                  <div key={idx} className="feature-card">
                    <h3>{feature.name}</h3>
                    <p className="details">{feature.details}</p>
                    <p className="benefits">
                      <strong>Benefits:</strong> {feature.benefits}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section className="hotels-cta">
              <h2>Upgrade Your Hotel Operations</h2>
              <p>
                Empower your hotel/lodge/guest house with Magyne, 
                streamline tasks and open the door to greater success.
              </p>
              
              <button
                className="cta-btn"
                onClick={handleNavigate}
                disabled={!isActive}
              >
                {product.status?.toUpperCase() === "PLANNED"
                  ? "Coming Soon"
                  : product.status?.toUpperCase() === "DEVELOPMENT"
                  ? "Coming Soon"
                  : "Choose a Package"}
              </button>

              </section>
            </>
        ) : !loading && !product && (
          /* Invalid Product */
          <div className="login-card mt-lg-5 mb-lg-5">

            <div className="logo-container">

              <h3 className="page-title mb-2">
                Invalid product.
              </h3>

              <p>
                The requested product could not be found.
                Please select a valid Magyne solution.
              </p>

              <p className="register-text">
                Explore our products{" "}
                <a href="/products">
                  See products
                </a>
              </p>

            </div>

          </div>
        )}

        <div className="bg-blobs">
          <div className="blob blob1"></div>
          <div className="blob blob2"></div>
          <div className="blob blob3"></div>
        </div>
      </div>
    </main>
    </>
  );
}
